import React from 'react';
import { motion } from 'framer-motion';
import { subDays } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiClock, FiAlertCircle, FiTrendingUp } = FiIcons;

const TasksReport = ({ tasks }) => {
  // Helper for real stats
  const calculateGrowth = (items) => {
    if (!items || items.length === 0) return { val: 0, type: 'neutral' };
    const now = new Date();
    const thirtyDaysAgo = subDays(now, 30);
    const sixtyDaysAgo = subDays(now, 60);

    const thisMonth = items.filter(i => new Date(i.createdAt) >= thirtyDaysAgo);
    const lastMonth = items.filter(i => {
      const d = new Date(i.createdAt);
      return d >= sixtyDaysAgo && d < thirtyDaysAgo;
    });

    const currentCount = thisMonth.length;
    const prevCount = lastMonth.length;

    if (prevCount === 0) return { val: currentCount > 0 ? 100 : 0, type: 'increase' };
    
    const diff = ((currentCount - prevCount) / prevCount) * 100;
    return { 
      val: Math.abs(Math.round(diff)), 
      type: diff >= 0 ? 'increase' : 'decrease' 
    };
  };

  const taskStats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    overdue: tasks.filter(t => new Date(t.deadline) < new Date() && t.status !== 'completed').length
  };

  const completionRate = taskStats.total > 0 ? Math.round((taskStats.completed / taskStats.total) * 100) : 0;
  
  const priorityBreakdown = {
    high: tasks.filter(t => t.priority === 'high').length,
    medium: tasks.filter(t => t.priority === 'medium').length,
    low: tasks.filter(t => t.priority === 'low').length
  };

  // Real Growth
  const totalGrowth = calculateGrowth(tasks);
  const completionGrowth = calculateGrowth(tasks.filter(t => t.status === 'completed'));
  const inProgressGrowth = calculateGrowth(tasks.filter(t => t.status === 'in_progress'));
  // Overdue growth logic: compare overdue creation dates? Or just count. 
  // Let's stick to creation date of tasks that ARE overdue.
  const overdueGrowth = calculateGrowth(tasks.filter(t => new Date(t.deadline) < new Date() && t.status !== 'completed'));

  const cards = [
    { 
      title: 'Total Tasks', 
      value: taskStats.total, 
      icon: FiCheckCircle, 
      color: 'blue', 
      change: `${totalGrowth.val}%`, 
      type: totalGrowth.type 
    },
    { 
      title: 'Completion Rate', 
      value: `${completionRate}%`, 
      icon: FiTrendingUp, 
      color: 'green', 
      change: `${completionGrowth.val}%`, 
      type: completionGrowth.type 
    },
    { 
      title: 'In Progress', 
      value: taskStats.inProgress, 
      icon: FiClock, 
      color: 'yellow', 
      change: `${inProgressGrowth.val}%`, 
      type: inProgressGrowth.type 
    },
    { 
      title: 'Overdue', 
      value: taskStats.overdue, 
      icon: FiAlertCircle, 
      color: 'red', 
      change: `${overdueGrowth.val}%`, 
      type: overdueGrowth.type 
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-500 text-blue-600 bg-blue-50',
    green: 'bg-green-500 text-green-600 bg-green-50',
    yellow: 'bg-yellow-500 text-yellow-600 bg-yellow-50',
    red: 'bg-red-500 text-red-600 bg-red-50'
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{card.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-sm font-medium ${
                    card.type === 'increase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {card.type === 'increase' ? '+' : '-'}{card.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">vs last 30 days</span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${colorClasses[card.color].split(' ')[2]}`}>
                <SafeIcon icon={card.icon} className={`w-6 h-6 ${colorClasses[card.color].split(' ')[1]}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Task Status Distribution */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Status Distribution</h3>
          <div className="space-y-4">
            {Object.entries(taskStats).filter(([key]) => !['total', 'overdue'].includes(key)).map(([status, count]) => {
              const percentage = taskStats.total > 0 ? Math.round((count / taskStats.total) * 100) : 0;
              const statusConfig = {
                completed: { label: 'Completed', color: 'bg-green-500' },
                inProgress: { label: 'In Progress', color: 'bg-blue-500' },
                pending: { label: 'Pending', color: 'bg-yellow-500' }
              };
              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{statusConfig[status].label}</span>
                    <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className={`h-2 rounded-full ${statusConfig[status].color}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Priority Breakdown */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Breakdown</h3>
          <div className="space-y-4">
            {Object.entries(priorityBreakdown).map(([priority, count]) => {
              const percentage = taskStats.total > 0 ? Math.round((count / taskStats.total) * 100) : 0;
              const priorityConfig = {
                high: { label: 'High Priority', color: 'bg-red-500' },
                medium: { label: 'Medium Priority', color: 'bg-yellow-500' },
                low: { label: 'Low Priority', color: 'bg-green-500' }
              };
              return (
                <div key={priority}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{priorityConfig[priority].label}</span>
                    <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                      className={`h-2 rounded-full ${priorityConfig[priority].color}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Recent Tasks Table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Tasks</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Task</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Deadline</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tasks.slice(0, 5).map(task => (
                <tr key={task.id}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {task.title}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      task.priority === 'high' ? 'bg-red-100 text-red-800' : 
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                      task.status === 'completed' ? 'bg-green-100 text-green-800' : 
                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {task.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {task.deadline ? new Date(task.deadline).toLocaleDateString() : 'No deadline'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default TasksReport;